/**
 * Chat API — payload building, API call with fallbacks, polling.
 */
import { devLog, devError } from './devLog';
import { ALL_AI_MODELS, OPENAI_STREAM } from '../config/chatConfig';
import { isStreamModeEnabled } from '../components/StreamModeToggle';
import memoryApi from '../api/memoryApi';
import { authedFetch } from '../api/authedFetch';

// Gate the ai_stream streaming path:
//   - Public (non-admin): always streams when global+model stream flags allow.
//   - Admin/owner: respects the per-browser StreamModeToggle so we can A/B test
//     the legacy ai_manager_v2 buffered path against ai_stream.
function _streamEligible(user, modelConfig) {
  if (!OPENAI_STREAM) return false;
  if (!modelConfig?.stream) return false;
  const role = user?.['custom:membership'];
  if (['admin', 'owner'].includes(role)) return isStreamModeEnabled();
  return true;
}

const EXTENDED_TIMEOUT_URL = 'https://api.limkc.com/extended/';
const STREAM_URL = 'https://stream.limkc.com/chat';  // ai_stream (OAC-fronted, response streaming)
const USE_EXTENDED_TIMEOUT = true;

// Required for POST to OAC-protected Lambda Function URL per AWS docs.
async function _sha256Hex(str) {
  const buf = new TextEncoder().encode(str);
  const hash = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Convert upload images to base64 array (max 4).
 */
export const convertImagesToBase64 = async (uploadImages) => {
  return Promise.all(
    uploadImages.slice(0, 4).map(img => new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(img);
    }))
  );
};

/**
 * Build payload for AI API call based on model.
 */
export const buildPayload = (input, selectedModel, activeSessionId, imagesData) => {
  const modelConfig = ALL_AI_MODELS[selectedModel];

  if (selectedModel === "deepseek-r1:32b") {
    return {
      model: selectedModel,
      prompt: `User: ${input.trim()}\nAssistant: `,
      temperature: 0.1,
      top_p: 0.9,
      max_tokens: 16384,
      stream: false,
      session_id: activeSessionId,
      timezone: getTimezone(),
    };
  }

  const messages = [{
    role: "user",
    content: input.trim(),
    ...(imagesData && imagesData.length > 0 ? { images: imagesData } : {}),
  }];

  return {
    model: selectedModel,
    messages,
    stream: modelConfig.stream,
    session_id: activeSessionId,
    timezone: getTimezone(),
  };
};

/**
 * Detect likely tool usage from user prompt text.
 * Returns 'weather', 'web_search', or null.
 */
export const detectToolHint = (input) => {
  const lower = input.trim().toLowerCase();
  if (lower.includes('weather') || lower.includes('temperature') || lower.includes('forecast')) {
    return 'weather';
  }
  if (lower.includes('current') || lower.includes('latest') || lower.includes('news') ||
      lower.includes('today') || lower.includes('now') || lower.includes('price') ||
      lower.includes('stock') || lower.includes('search')) {
    return 'web_search';
  }
  return null;
};

/**
 * Call AI API with extended timeout + kaidex fallback logic.
 * Returns the fetch Response object.
 */
export const callApi = async (payload, selectedModel, abortSignal, user = null) => {
  const modelConfig = ALL_AI_MODELS[selectedModel];

  // Streaming path — ai_stream Lambda via stream.limkc.com.
  // Gated by admin membership + localStorage toggle + modelConfig.stream + OPENAI_STREAM.
  // Auth via X-Auth-Token (not Authorization — that header is consumed by CloudFront OAC
  // SigV4 signing upstream). x-amz-content-sha256 is REQUIRED for POST per AWS OAC spec.
  if (_streamEligible(user, modelConfig) && modelConfig.endpoint.includes('/ai')) {
    const body = JSON.stringify(payload);
    const bodyHash = await _sha256Hex(body);
    devLog(`🌊 Streaming: ${STREAM_URL} model=${selectedModel}`);
    return authedFetch(STREAM_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-amz-content-sha256': bodyHash,
      },
      body,
      signal: abortSignal,
    });
  }

  if (USE_EXTENDED_TIMEOUT && modelConfig.endpoint.includes('/ai')) {
    // Force stream:false on the legacy ai_manager_v2 path. Its _handle_streaming
    // is broken (buffers + escaped newlines + no DynamoDB save). Streaming belongs
    // only on ai_stream. When admin toggle is OFF, we land here regardless of
    // model config's stream flag.
    const nonStreamPayload = { ...payload, stream: false };
    devLog(`⏰ Extended timeout (120s): ${EXTENDED_TIMEOUT_URL}`);
    devLog(`📦 Payload:`, nonStreamPayload);

    return authedFetch(EXTENDED_TIMEOUT_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(nonStreamPayload),
      signal: abortSignal,
    });
  }

  // Kaidex-local fallback chain
  if (selectedModel === "kaidex-local") {
    return callWithKaidexFallback(payload, abortSignal);
  }

  // Direct call
  devLog(`🚀 Making API call to: ${modelConfig.endpoint}`);
  return authedFetch(modelConfig.endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(payload),
    signal: abortSignal,
  });
};

/**
 * Kaidex-local: try localhost → LAN → cloud fallback.
 */
async function callWithKaidexFallback(payload, abortSignal) {
  const FALLBACK_ENDPOINTS = [
    { name: "localhost:11453", url: "http://localhost:11453/v1/chat/completions" },
    { name: "LAN:11435", url: "http://192.168.20.11:11435/v1/chat/completions" },
  ];

  for (const endpoint of FALLBACK_ENDPOINTS) {
    try {
      devLog(`🔄 Trying kaidex-local ${endpoint.name}...`);
      // Local kaidex endpoints (localhost / LAN) don't need Cognito auth.
      const response = await fetch(endpoint.url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload),
        signal: abortSignal,
      });

      if (!response.ok) throw new Error(`Server error: ${response.status}`);
      devLog(`✅ kaidex-local ${endpoint.name} succeeded`);
      return response;
    } catch (err) {
      devLog(`⚠️ kaidex-local ${endpoint.name} failed: ${err.message}`);
    }
  }

  // All local failed — fall back to cloud
  devLog(`⚠️ All local endpoints failed, falling back to gpt-4.1-mini`);
  const fallbackModel = "gpt-4.1-mini";
  const fallbackConfig = ALL_AI_MODELS[fallbackModel];
  payload.model = fallbackModel;

  return authedFetch(fallbackConfig.endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(payload),
    signal: abortSignal,
  });
}

/**
 * Poll for new messages after extended timeout failure.
 * Backend Lambda continues processing even after API Gateway timeout.
 */
export const pollForResponse = (sessionId, initialMessageCount, { setMessages, setIsLoading, mockTimeoutPolling }) => {
  let pollCount = 0;
  const maxPolls = 24;

  const sanitizeMessages = (msgs) => msgs.map((msg) => {
    if (msg.text && msg.text.startsWith('{"') && msg.text.includes('"image":')) {
      try {
        const parsed = JSON.parse(msg.text);
        return { role: msg.role, text: parsed.text || "Look at this image and elaborate", image: parsed.image, timestamp: msg.timestamp };
      } catch (e) { return msg; }
    }
    if (msg.image && typeof msg.image === 'string' && msg.image.startsWith('blob:')) {
      const { image, ...rest } = msg;
      return rest;
    }
    return msg;
  });

  const poll = async () => {
    try {
      pollCount++;
      devLog(`🔍 Polling for new messages (${pollCount}/${maxPolls}) for session ${sessionId}`);

      const latestMessages = await memoryApi.getMessages(sessionId);

      if (Array.isArray(latestMessages) && latestMessages.length > initialMessageCount) {
        devLog(`✅ New messages found! ${latestMessages.length} total`);
        setMessages(sanitizeMessages(latestMessages));
        setIsLoading(false);
        mockTimeoutPolling.current = false;
        return;
      }

      if (pollCount < maxPolls) {
        setTimeout(poll, 5000);
      } else {
        setIsLoading(false);
        mockTimeoutPolling.current = false;
        devLog('⏰ Polling stopped - refresh page to see response');
      }
    } catch (error) {
      devError('Polling error:', error);
      if (pollCount < maxPolls) setTimeout(poll, 5000);
    }
  };

  // Start polling after 15 seconds
  setTimeout(poll, 15000);
};

function getTimezone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone; }
  catch (e) { return 'UTC'; }
}
